import { ConditionCard } from '@/components/admin/experiences/page-editor/condition-card';
import { ConditionPalette } from '@/components/admin/experiences/page-editor/condition-palette';
import { PAGE_EDITOR_IDS } from '@/components/admin/experiences/page-editor/contracts';
import { TooltipShield } from '@/components/admin/tooltip-shield';
import {
  canAddCondition,
  conditionPalette,
  offersConditions,
  withConditions,
} from '@/lib/display-conditions';
import { countLabel } from '@/lib/experience-labels';
import { cn } from '@/lib/utils';
import type { VariableSources } from '@/lib/variable-pill';
import type { ExperienceBlockDraft } from '@matter_extractor/shared';
import { EXPERIENCE_BLOCK_CONDITIONS_MAX } from '@matter_extractor/shared';
import { Alert, Button, Icon, Tooltip } from '@pms-digital/design-system';
import { useState } from 'react';

/**
 * La section « Conditions » de l'inspecteur d'un bloc (refonte modulaire §9.2,
 * H67 à H71 ; maquettes `EditeurConditions`, `InspecteurInputNom`,
 * `ConditionsParNature` ; issue #150).
 *
 * « Toujours affiché » par défaut, « Ajouter une condition » : a list of
 * conditions, ALL true for the block to show (the « et » between them, the
 * « ou » inside a condition, by its values). Folded while the block carries
 * none - the Input of `InspecteurInputNom` - and open as soon as it carries one,
 * the image of `EditeurConditions`.
 *
 * ## The way out of a page offers none (H69)
 *
 * A Bouton and a Noyau block in a choice mode say so in place of the section:
 * an absent section would be a question, a sentence is the answer. And if such
 * a block CARRIES conditions all the same - a draft written by the assistant,
 * a block pasted from elsewhere - the section shows what the save will refuse
 * (`condition-on-exit`) and the one gesture that repairs it, since nothing else
 * on this screen could reach those conditions.
 *
 * ## Its state is the block's
 *
 * Folded or open, the palette open or not: posture of the screen, not content.
 * The inspector mounts it with the block's id for a `key`, so selecting another
 * block starts from that block's own posture rather than from the previous one.
 */

const HINT = 'text-gray-600 text-caption leading-4';

export interface ConditionsSectionProps {
  readonly block: ExperienceBlockDraft;
  readonly sources: VariableSources;
  /** The page the block stands on - what the palette's reach is cut at (H60). */
  readonly pageIndex: number;
  readonly readOnly: boolean;
  readonly onChange: (next: ExperienceBlockDraft) => void;
}

export function ConditionsSection({
  block,
  sources,
  pageIndex,
  readOnly,
  onChange,
}: ConditionsSectionProps) {
  const count = block.conditions.length;
  const [open, setOpen] = useState(count > 0);
  const [adding, setAdding] = useState(false);

  if (!offersConditions(block)) {
    const noun = block.type === 'button' ? 'Un bouton' : 'Un choix du noyau';
    return (
      <section data-testid={PAGE_EDITOR_IDS.conditions} data-exit="true" className="space-y-1.5">
        <p className="font-semibold text-gray-800 text-small">Conditions</p>
        <p className={HINT}>
          {noun} ne porte aucune condition : la sortie d’une page ne se masque pas, sans quoi une
          personne pourrait rester sans issue.
        </p>
        {count === 0 ? null : (
          <Alert tone="danger">
            <p>
              Ce bloc porte {countLabel(count, 'condition')}, que l’enregistrement d’une version
              refusera.
            </p>
            {readOnly ? null : (
              <div className="mt-2">
                <Button
                  type="button"
                  variant="accent"
                  size="sm"
                  data-testid={PAGE_EDITOR_IDS.conditionsExitClear}
                  onClick={() => onChange(withConditions(block, []))}
                >
                  Retirer les conditions
                </Button>
              </div>
            )}
          </Alert>
        )}
      </section>
    );
  }

  const full = !canAddCondition(block);

  return (
    <section data-testid={PAGE_EDITOR_IDS.conditions} className="space-y-2">
      <button
        type="button"
        data-testid={PAGE_EDITOR_IDS.conditionsToggle}
        aria-expanded={open}
        onClick={() => {
          setOpen((was) => !was);
          setAdding(false);
        }}
        className="flex w-full cursor-pointer items-center gap-2 rounded-md py-1 text-left focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-bleu-nuit"
      >
        <span
          className={cn(
            'inline-flex shrink-0 text-gray-600 transition-transform',
            open ? 'rotate-90' : '',
          )}
        >
          <Icon name="chevron_right" size={16} />
        </span>
        <span className="font-semibold text-gray-800 text-small">Conditions</span>
        <span
          className={cn(
            'ml-auto shrink-0 rounded-full px-2 font-semibold text-caption leading-5',
            count === 0 ? 'bg-gray-100 text-gray-700' : 'bg-bleu-nuit-tint text-bleu-nuit',
          )}
        >
          {count === 0 ? 'Toujours affiché' : countLabel(count, 'condition')}
        </span>
      </button>

      {open ? (
        <div className="space-y-2">
          <p className={HINT}>Afficher ce bloc si toutes ces conditions sont vraies.</p>

          {block.conditions.map((condition, index) => (
            <ConditionCard
              // A condition has no id: its rank IS its address in the list, and
              // the list only changes by one whole gesture at a time.
              // biome-ignore lint/suspicious/noArrayIndexKey: see above.
              key={index}
              condition={condition}
              sources={sources}
              pageIndex={pageIndex}
              readOnly={readOnly}
              onChange={(next) =>
                onChange(
                  withConditions(
                    block,
                    block.conditions.map((one, rank) => (rank === index ? next : one)),
                  ),
                )
              }
              onRemove={() =>
                onChange(
                  withConditions(
                    block,
                    block.conditions.filter((_, rank) => rank !== index),
                  ),
                )
              }
            />
          ))}

          {readOnly ? null : adding ? (
            <ConditionPalette
              palette={(query) => conditionPalette(sources, pageIndex, block, query)}
              onPick={(condition) => {
                onChange(withConditions(block, [...block.conditions, condition]));
                setAdding(false);
              }}
              onClose={() => setAdding(false)}
            />
          ) : full ? (
            <Tooltip content={`Au plus ${EXPERIENCE_BLOCK_CONDITIONS_MAX} conditions par bloc.`}>
              <TooltipShield closed layout="flex w-full">
                <AddButton disabled onClick={() => undefined} />
              </TooltipShield>
            </Tooltip>
          ) : (
            <AddButton disabled={false} onClick={() => setAdding(true)} />
          )}

          <p className={HINT}>Sans condition, le bloc est toujours affiché.</p>
        </div>
      ) : null}
    </section>
  );
}

function AddButton({
  disabled,
  onClick,
}: {
  readonly disabled: boolean;
  readonly onClick: () => void;
}) {
  return (
    <button
      type="button"
      data-testid={PAGE_EDITOR_IDS.conditionAdd}
      disabled={disabled}
      onClick={onClick}
      className="flex h-9 w-full cursor-pointer items-center justify-center gap-1.5 rounded-md border-2 border-bleu-nuit border-dashed bg-white font-semibold text-bleu-nuit text-small hover:bg-bleu-nuit-tint focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-bleu-nuit disabled:cursor-not-allowed disabled:border-gray-300 disabled:text-gray-500 disabled:hover:bg-white"
    >
      <Icon name="add" size={16} />
      Ajouter une condition
    </button>
  );
}
